import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Dataset, DatasetSubtype, DatasetType } from 'src/app/modules/core/store/models/dataset.model';
import { RelatedDocument, SchemaField, SystemFieldNames } from 'src/app/modules/core/store/models/document.model';
import { Document, DocumentReader, FieldDataType, FilterGroup, FilterGroupType, View } from 'src/app/modules/core/store/models';
import { TranslateService } from '@ngx-translate/core';
import { DataApiService as MainDataApiService } from 'src/app/modules/management/store/data/data-api.service';
import { RelatedViewsService } from '../services/related-views.service';
import { RelatedDocumentCard, RelatedDocumentCardsSection, RelatedDocumentsView } from '../related-ducument-model';
import { DocumentQueryParams } from '@app/modules/core/store/models';
import { DatasetApiService } from '@app/modules/core/services/dataset-api.service';
import { map, switchMap, take, tap } from 'rxjs/operators';

@Component({
    selector: 'topo-related-documents',
    templateUrl: './related-documents.component.html',
    styleUrls: ['./related-documents.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class RelatedDocumentsComponent implements OnInit, OnDestroy {
  @Input() parentDocument: Document;
  @Input() parentDataset: Dataset;
  @Input() layers: any[];
  @Input() set targetDatasetUIds(value: string[]) {
    if (this._targetDatasetUIds !== value) {
      this._targetDatasetUIds = value;
      this.loadAllRelatedDocuments();
    }
  }
  _targetDatasetUIds: string[];

  _searchTerm: string;
  @Input() set searchTearm(value: string) {
    if (this._searchTerm !== value) {
      this._searchTerm = value;
      this.loadAllRelatedDocuments();
    }
  }
  @Output() zoomToDocument: EventEmitter<RelatedDocumentCard> = new EventEmitter();

  displayOptions?: {
    [x: string]: any;
  };

  @Output() open: EventEmitter<any> = new EventEmitter();

  relatedDocuments: RelatedDocument[];
  relatedDatasets?: Dataset[];
  relDocumentsView?: RelatedDocumentsView;
  targetDataset: Dataset;
  expandedDatasetUIds: string[];

  constructor(
    public translate: TranslateService,
    private relatedViewsService: RelatedViewsService,
    private changeDetector: ChangeDetectorRef,
    private datasetService: DatasetApiService,
    private mainDataApiService: MainDataApiService
  ) {}

  ngOnInit(): void {}

  ngOnDestroy(): void {}

  onClickCard(card: RelatedDocumentCard): void {
    this.open.emit({ dataset: card.dataset, document: card.document, layer: card.layer });
  }

  onOpen(document: RelatedDocument): void {
    this.open.emit(document);
  }

  onExpandCollapse(group: RelatedDocumentCardsSection): void {
    if (this.expandedDatasetUIds?.some(d => d === group.dataset?.uId)) {
      const index = this.expandedDatasetUIds.findIndex(d => d === group.dataset?.uId);
      if (index >= 0) {
        this.expandedDatasetUIds.splice(index, 1);
      }
    } else {
      if (!!!this.expandedDatasetUIds) {
        this.expandedDatasetUIds = [];
      }
      this.expandedDatasetUIds.push(group.dataset?.uId);
    }
    this.changeDetector.markForCheck();
  }

  isSectionExpanded(group: RelatedDocumentCardsSection): boolean {
    return this.expandedDatasetUIds?.some(d => d === group.dataset?.uId);
  }

  loadAllRelatedDocuments() {
    this.relDocumentsView = {
      sections: []
    };
    const referenceFields = this.parentDataset?.schema?.fields?.filter(f => f.dataType === FieldDataType.ReferenceCriteria);
    referenceFields
      .filter(f => !!f.settings?.targetDatasetUId)
      .forEach(f => {
        this.relDocumentsView.sections.push({
          datasetUId: f.settings.targetDatasetUId,
          refField: f.systemName,
          icon: {}
        } as RelatedDocumentCardsSection);
      });

    this._targetDatasetUIds.forEach(uid => {
      this.relDocumentsView.sections.push({
        datasetUId: uid,
        refField: null,
        icon: {}
      } as RelatedDocumentCardsSection);
    });

    referenceFields
      .filter(f => !!f.settings?.targetDatasetUId)
      .forEach(f => {
        this.loadRefDocuments(f);
      });

    this._targetDatasetUIds.forEach(uid => {
      this.loadRelatedDocuments(uid);
    });
  }

  loadRefDocuments(field: SchemaField) {
    let dataset: Dataset;

    this.datasetService
      .getDataset(field.settings?.targetDatasetUId, { includeWorkflow: true })
      .pipe(
        take(1),
        map(ds => {
          dataset = ds;
          const convertedFilter = !!field.settings?.filterGroup
            ? this.relatedViewsService.convertReferenceFilterToRegularFilter(field.settings?.filterGroup, this.parentDocument)
            : null;

          if (!!this._searchTerm) {
            const searchGroup = this.relatedViewsService.generateSearchClause(this._searchTerm, dataset);
            if (!!searchGroup) {
              const group: FilterGroup = {
                filterGroups: [],
                filters: [],
                groupType: FilterGroupType.And
              };
              if (!!convertedFilter) {
                group.filterGroups.push(convertedFilter);
              }
              group.filterGroups.push(searchGroup);
              return group;
            }
          }

          return convertedFilter;
        }),
        switchMap(filterGroup => {
          const sortFieldName = this.relatedViewsService.getDatasetSortField(dataset);
          const params: DocumentQueryParams = {
            filterGroup,
            fields: null,
            pagination: {
              skip: 0,
              take: 100
            },
            sort: !!sortFieldName ? `${sortFieldName} ASC` : null
          };

          return this.mainDataApiService.getDocuments(dataset.uId, params);
        }),
        tap(data => {
          const layer = !!this.layers ? this.layers.find(l => l.datasetUId === dataset?.uId) : null;
          const section = this.relDocumentsView.sections.find(s => s.datasetUId === dataset?.uId && s.refField === field.systemName);
          if (!!section) {
            section.dataset = dataset;
            section.layer = layer;
            section.color = dataset?.defaultColor;
            section.iconPath = dataset?.iconPath;
            section.isCustomIcon = dataset?.isCustomIcon;
            section.cards = this.getDocumentCards(dataset, data.data, layer);
            section.view = dataset.workflow?.views.find(vw => vw.name === 'card');
            section.icon = this.relatedViewsService.getSectionIcon(dataset);
            section.count = !!data ? data.totalCount : 0;
          }

          this.changeDetector.markForCheck();
        })
      )
      .subscribe();
  }

  loadRelatedDocuments(targetDatasetUId: string) {
    let dataset: Dataset;

    this.datasetService
      .getDataset(targetDatasetUId, { includeWorkflow: true })
      .pipe(
        take(1),
        map(ds => {
          dataset = ds;
          const filterGroup = {
            filterGroups: [],
            filters: [],
            groupType: FilterGroupType.And
          } as FilterGroup;

          if (!!this._searchTerm) {
            filterGroup.filterGroups.push(this.relatedViewsService.generateSearchClause(this._searchTerm, dataset));
          }
          return filterGroup;
        }),
        switchMap(filterGroup => {
          const sortFieldName = this.relatedViewsService.getDatasetSortField(dataset);
          const params: DocumentQueryParams = {
            filterGroup,
            fields: null,
            pagination: {
              skip: 0,
              take: 100
            },
            sort: !!sortFieldName ? `${sortFieldName} ASC` : null
          };

          return this.mainDataApiService.getRelatedDocuments(
            this.parentDataset.uId,
            this.parentDocument.uid,
            targetDatasetUId,
            params,
            true
          );
        }),
        tap(data => {
          const layer = !!this.layers ? this.layers.find(l => l.datasetUId === dataset?.uId) : null;
          const section = this.relDocumentsView.sections.find(s => s.datasetUId === dataset?.uId && s.refField === null);
          if (!!section) {
            section.dataset = dataset;
            section.layer = layer;
            section.color = dataset?.defaultColor;
            section.iconPath = dataset?.iconPath;
            section.isCustomIcon = dataset?.isCustomIcon;
            section.cards = this.getDocumentCards(dataset, data.data, layer);
            section.view = dataset.workflow?.views.find(vw => vw.name === 'card');
            section.icon = this.relatedViewsService.getSectionIcon(dataset);
            section.count = !!data ? data.totalCount : 0;
          }

          this.changeDetector.markForCheck();
        })
      )
      .subscribe();
  }

  private getDocumentCards(dataset: Dataset, documents: Document[], layer: any): RelatedDocumentCard[] {
    return documents?.map(d => {
      return {
        dataset: dataset,
        view: dataset?.workflow?.views.find(vw => vw.name === 'card'),
        document: d,
        layer: layer
      } as RelatedDocumentCard;
    });
  }

  getDisplayText(datasetUId: string, document: Document): string {
    const displayOptions = this.displayOptions[datasetUId];

    let displayString = displayOptions.template;

    Object.keys(document).forEach(k => {
      displayString = displayString.replace(`{${k}}`, document[k]);
    });

    const regex = /\{.*?\}/g;
    displayString = displayString.replace(regex, '');

    return displayString;
  }

  onZoomToDocument(card: RelatedDocumentCard) {
    this.zoomToDocument.emit(card);
  }

  isPersonCard(card: RelatedDocumentCard): boolean {
    return card?.dataset?.subtype === DatasetSubtype.Person || card?.dataset?.subtype === DatasetSubtype.HorizonPerson;
  }

  isFacilityCard(card: RelatedDocumentCard): boolean {
    return card?.dataset?.subtype === DatasetSubtype.Facility;
  }

  isIncidentCard(card: RelatedDocumentCard): boolean {
    return card?.dataset?.type === DatasetType.Incident;
  }

  isNormalCard(card: RelatedDocumentCard): boolean {
    return !this.isPersonCard(card) && !this.isFacilityCard(card) && !this.isIncidentCard(card);
  }

  /*  public zoomToDocument(document: RelatedDocument) {
    this.store.dispatch(fromMap.hideTableShowCardsIfDataLoaded());

    this.store.dispatch(
      fromMap.zoomToLayerDocuments({
        layerDocuments: [
          {
            layerUId: this.layerUId,
            documents: [document.document]
          }
        ]
      })
    );
  }

  private getLocation(dataset: Dataset, document: any): string {
    const reader = new DocumentReader(document);

    const geoField = dataset.schema.fields.find(f => f.dataType === FieldDataType.Geometry);
    if (!!geoField) {
      const geoValue = reader.getFieldValue(geoField.systemName);
      if (!!geoValue) {
        return this.getLatLngForDisplay(geoValue);
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  private getLatLngForDisplay(geometry: any) {
    const data = JSON.parse(geometry);
    const normalizedData = this.utilService.getNormalizedGeometriesOrFeaturesFromGeoJson(data);
    const bounds = this.distanceService.getBoundsForGeometryFeatures(normalizedData);
    if (!bounds) {
      return null;
    }

    const center = bounds.getCenter();
    const lat = numeral(center.lat());
    const lng = numeral(center.lng());

    return `${lat.format('0[.]0000')}, ${lng.format('0[.]0000')}`;
  }*/
}
